import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Immutable from 'immutable';
import animateScrollTo from 'animated-scroll-to';

import { pageContext } from 'ctm-util-functions';

import { CompareButton } from './compare_button/compare_button';
import { ProductCompareWrapper } from './product_compare_wrapper/product_compare_wrapper';
import { Placeholder } from './placeholder/placeholder';
import { Product } from './product/product';
import { ExitCompare } from './exit_compare/exit_compare';

class ProductCompare extends Component {

    constructor(...args) {
        super(...args);
        const { models } = pageContext.getProps();
        const baseAssetsPath = models.pageSchemaModel.getBaseAssetsPath();
        const vertical = pageContext.getVertical();
        this.logoSrcPath = `${baseAssetsPath}/images/logos/${vertical}`;
        this.state = {
            alertUserToSelectProduct: false
        };
    }

    /**
     * You can define a minimum number of products before we enable comparison per vertical.
     * @returns {Boolean} True if we should allow them to compare.
     */
    isCompareAvailable = () => {
        const { products, contentProps } = this.props;
        return products.size >= contentProps.minProducts;
    };

    /**
     * Based on the number of products selected, toggle the compare button to active state.
     */
    handleCompareClick = () => {
        const { toggleCompare } = this.props;
        if (this.isCompareAvailable()) {
            toggleCompare(true);
        } else {
            // Alert user to select another product (using animation as it feels more intuitive).
            this.setState({ alertUserToSelectProduct: true }, () => {
                setTimeout(() => {
                    this.setState({ alertUserToSelectProduct: false });
                }, 1000);
            });
        }
    };

    /**
     * Toggle the compare to inactive state
     */
    handleExitCompareClick = () => {
        const { toggleCompare } = this.props;
        toggleCompare(false);
        animateScrollTo(0);
    };

    /**
     * Remove the product from the comparison queue and close it if necessary.
     * @param {String} productId The product id
     */
    handleRemoveClick = (productId) => {
        const { removeProduct, products, contentProps, toggleCompare } = this.props;
        removeProduct(productId);
        if (products.size - 1 < contentProps.minProducts) {
            toggleCompare(false);
        }
    };

    /**
     * Determines what to render in the comparison queue.
     * @param {Number} index The products index in the compare queue
     * @returns {Node} A product component or placeholder
     */
    renderProduct = (index) => {
        const { alertUserToSelectProduct } = this.state;
        const { frequency, products } = this.props;
        const product = products.get(index);
        if (product) {
            /** The Carpeesh product card should be removed from the Compare Modal when the payment type of 'Monthly' is selected
             * https://dev.azure.com/ctmaus/CTM/_workitems/edit/7764
             * For Home and content CHU product just need to add extra condition as below
             * pageContext.getVertical() === 'car-insurance' || pageContext.getVertical() === 'home-contents-insurance'
            */
            if (pageContext.getVertical() === 'car-insurance' || pageContext.getVertical() === 'home-contents-insurance') {
                const productId = product.getIn(['productMeta', 'id']);
                const isMonthlyAvailable = product.getIn(['productMeta', 'price', 'monthlyAvailable']) !== false;
                if (frequency === 'monthly' && !isMonthlyAvailable) {
                    this.handleRemoveClick(productId);
                    return <></>;
                }
            }
            return (
                <Product
                    key={index}
                    product={product}
                    frequency={frequency}
                    logoSrcPath={this.logoSrcPath}
                    removeProduct={this.handleRemoveClick}
                />
            );
        }
        return <Placeholder key={index} animate={alertUserToSelectProduct && index === 1} />;
    };

    /**
     * Not sure what the apply is @todo someone fix this.
     * @returns {Array} An array of product or placeholder components
     */
    renderProducts = () => {
        const { contentProps } = this.props;
        return Array
            .apply(this, { length: contentProps.maxProducts })
            .map((val, i) => this.renderProduct(i));
    };

    render = () => {
        if (pageContext.getProps().pageState.breakpoint === 'xs') {
            return null;
        }

        const { compareModeActive = false, products = Immutable.List() } = this.props;
        const isVisible = !!products.size;

        if (compareModeActive) {
            return (
                <ProductCompareWrapper isVisible={isVisible}>
                    <ExitCompare handleClick={this.handleExitCompareClick} />
                </ProductCompareWrapper>
            );
        }

        return (
            <ProductCompareWrapper isVisible={isVisible}>
                {this.renderProducts()}
                <CompareButton
                    handleClick={this.handleCompareClick}
                    isActive={this.isCompareAvailable()}
                />
            </ProductCompareWrapper>
        );
    };

}
ProductCompare.propTypes = {
    compareModeActive: PropTypes.bool,
    contentProps: PropTypes.shape({
        maxProducts: PropTypes.number.isRequired,
        minProducts: PropTypes.number.isRequired
    }).isRequired,
    frequency: PropTypes.string.isRequired,
    toggleCompare: PropTypes.func.isRequired,
    products: PropTypes.instanceOf(Immutable.List),
    removeProduct: PropTypes.func.isRequired
};
export { ProductCompare };
